import { z } from 'zod';
import { localeSchema, timestampSchema } from './common';

/**
 * Outbound notifications: what the product will send, and how somebody says
 * they would rather it did not.
 *
 * Everything the application knew was already in the database and nothing ever
 * left it. An invited user was created with status `invited` and never told; a
 * schedule change waiting for somebody's agreement sat in a mailbox that only
 * opens if they happen to sign in. Every collaborative feature here -- change
 * sessions, approvals, agendas, mentions, comments, votes -- depended on
 * someone deciding to look.
 *
 * ## The shape
 *
 * A **category** is what a person opts out of, not what the code emits. Those
 * are different granularities on purpose: "tell me when my agreement is needed"
 * is a decision somebody can hold an opinion about, and `approval_granted`
 * versus `approval_declined` is not. So the catalog below is short and stays
 * short, while the event types feeding it can grow freely.
 *
 * ## Opt-out, not opt-in
 *
 * A category is on until the recipient turns it off, and a category added later
 * is on for everybody. That is the right default for a tool people are
 * accountable to -- the failure mode of opt-in is a project where nobody was
 * told and everybody had technically consented to that -- and it is what makes
 * the stored form a list of *exceptions* rather than a row per user per
 * category that has to be backfilled every time the catalog grows.
 *
 * The price is that a new category starts switched on, which is only acceptable
 * because `transactional` exists to keep the list honest: anything a person
 * cannot refuse must say so here, so that everything else is genuinely
 * refusable.
 */

/* ------------------------------------------------------------- categories */

export interface NotificationCategoryDefinition {
  key: string;
  titleKey: string;
  descriptionKey: string;
  /**
   * Whether the recipient may switch it off.
   *
   * True for the messages that carry access itself. An invitation nobody can
   * receive is an account nobody can use, and "you have been given a role" is
   * how a person learns what they may now do -- turning those off would be a
   * setting that quietly breaks the product for the person who set it.
   *
   * Deliberately only that. A transactional flag is the standard way this kind
   * of catalog rots: every category's author believes theirs is essential, and
   * a year later nothing is refusable.
   */
  transactional: boolean;
}

export const NOTIFICATION_CATEGORIES = {
  account: {
    key: 'account',
    titleKey: 'notifications.categories.account.title',
    descriptionKey: 'notifications.categories.account.description',
    transactional: true,
  },
  schedule_approval: {
    key: 'schedule_approval',
    titleKey: 'notifications.categories.scheduleApproval.title',
    descriptionKey: 'notifications.categories.scheduleApproval.description',
    transactional: false,
  },
  schedule_change: {
    key: 'schedule_change',
    titleKey: 'notifications.categories.scheduleChange.title',
    descriptionKey: 'notifications.categories.scheduleChange.description',
    transactional: false,
  },
  /**
   * Documents somebody follows (docs/document-register-design.md section 5.4).
   *
   * One category covering both the reissue and the edit, because "tell me
   * about the drawings I follow" is the opinion a person actually holds --
   * "reissued but not retitled" is not, and offering it would be two switches
   * where nobody wants a second one.
   *
   * Which documents is a separate question, and it has a separate answer:
   * following. A category is the blunt instrument; the subscription is the
   * sharp one (`subscriptions.ts`).
   */
  /**
   * The weekly nudge about work that is running and unreported
   * (docs/schedule-progress-design.md section 12).
   *
   * One digest per owner per reporting day, never one message per task: a
   * notification per row is how somebody learns to filter the sender, and then
   * they stop seeing the approval requests too. Optional, and its project-level
   * switch is off until somebody sets a reporting day.
   */
  progress_reminder: {
    key: 'progress_reminder',
    titleKey: 'notifications.categories.progressReminder.title',
    descriptionKey: 'notifications.categories.progressReminder.description',
    transactional: false,
  },
  document_change: {
    key: 'document_change',
    titleKey: 'notifications.categories.documentChange.title',
    descriptionKey: 'notifications.categories.documentChange.description',
    transactional: false,
  },
} as const satisfies Record<string, NotificationCategoryDefinition>;

export type NotificationCategory = keyof typeof NOTIFICATION_CATEGORIES;
export const NOTIFICATION_CATEGORY_KEYS = Object.keys(
  NOTIFICATION_CATEGORIES,
) as NotificationCategory[];
export const notificationCategorySchema = z.enum(
  NOTIFICATION_CATEGORY_KEYS as [NotificationCategory, ...NotificationCategory[]],
);

/** The categories a person is allowed to switch off. */
export const OPTIONAL_NOTIFICATION_CATEGORIES = NOTIFICATION_CATEGORY_KEYS.filter(
  (key) => !NOTIFICATION_CATEGORIES[key].transactional,
);

export function isTransactionalCategory(category: NotificationCategory): boolean {
  return NOTIFICATION_CATEGORIES[category].transactional;
}

/**
 * Whether this person should be emailed about this category.
 *
 * One function, called by the only code that sends. A transactional category
 * ignores the opt-out list entirely rather than trusting that nothing ever put
 * one in it -- the API refuses to store one, and this makes a stored one
 * harmless rather than a silently disabled invitation.
 */
export function wantsEmail(category: NotificationCategory, optedOut: readonly string[]): boolean {
  if (isTransactionalCategory(category)) return true;
  return !optedOut.includes(category);
}

/* ------------------------------------------------------------ preferences */

export const notificationPreferencesSchema = z.object({
  /** Categories this person has switched off. Never contains a transactional one. */
  emailOptOut: z.array(notificationCategorySchema),
});
export type NotificationPreferences = z.infer<typeof notificationPreferencesSchema>;

export const updateNotificationPreferencesSchema = z
  .object({
    emailOptOut: z
      .array(notificationCategorySchema)
      .max(NOTIFICATION_CATEGORY_KEYS.length)
      .refine((values) => values.every((value) => !isTransactionalCategory(value)), {
        message: 'a transactional category cannot be switched off',
      })
      /* Sent as a set, stored as a set. Accepting duplicates would make two
       * equal preferences compare unequal in the audit metadata. */
      .transform((values) => [...new Set(values)]),
  })
  .strict();
export type UpdateNotificationPreferences = z.infer<typeof updateNotificationPreferencesSchema>;

/* -------------------------------------------------------------- deliveries */

/**
 * What became of one outbound message.
 *
 * `skipped` is the member that earns its place, and it is the same distinction
 * `AutomationRun` records for runs that did nothing: an email nobody was sent
 * because the recipient opted out, and an email nobody was sent because the
 * deployment has no mail server, and an email that failed, are three different
 * situations. A status of `sent` for all three -- or a missing row -- turns
 * "why did I not hear about this" into an unanswerable question.
 */
export const EMAIL_DELIVERY_STATUSES = ['pending', 'sent', 'failed', 'skipped'] as const;
export const emailDeliveryStatusSchema = z.enum(EMAIL_DELIVERY_STATUSES);
export type EmailDeliveryStatus = (typeof EMAIL_DELIVERY_STATUSES)[number];

/** Why a message was not sent. Machine-readable; the reader sees a sentence. */
export const EMAIL_SKIP_REASONS = ['opted_out', 'mail_disabled', 'no_address'] as const;
export const emailSkipReasonSchema = z.enum(EMAIL_SKIP_REASONS);
export type EmailSkipReason = (typeof EMAIL_SKIP_REASONS)[number];

export const emailDeliverySchema = z.object({
  id: z.string().uuid(),
  recipientUserId: z.string().uuid().nullable(),
  recipientEmail: z.string(),
  category: notificationCategorySchema,
  eventType: z.string(),
  locale: localeSchema,
  status: emailDeliveryStatusSchema,
  skipReason: emailSkipReasonSchema.nullable(),
  attemptCount: z.number().int().nonnegative(),
  lastErrorCode: z.string().nullable(),
  createdAt: timestampSchema,
  sentAt: timestampSchema.nullable(),
});
export type EmailDelivery = z.infer<typeof emailDeliverySchema>;

/* ------------------------------------------------------------ the payloads */

/**
 * What an outbox message carries into the delivery job.
 *
 * Everything the renderer needs, resolved at the moment the event happened: the
 * recipient's address and language, and the handful of strings the message is
 * about. Nothing is looked up again at send time except the opt-out list, which
 * must be read as late as possible so that turning a category off silences
 * messages already queued.
 *
 * That is why the payload carries a name rather than a user id for the *actor*:
 * a delivery retried tomorrow must describe what happened yesterday, not what
 * the database says today.
 */
export const notificationRequestSchema = z.object({
  category: notificationCategorySchema,
  /** The specific thing that happened, for the renderer to switch on. */
  eventType: z.string().min(1).max(120),
  recipientUserId: z.string().uuid().nullable(),
  recipientEmail: z.string().email(),
  recipientLocale: localeSchema,
  /**
   * Interpolation values for the template, already resolved to strings. Bounded
   * because this is stored and replayed, and a payload that can grow without
   * limit is a queue that can be filled by one badly named task.
   */
  params: z.record(z.string(), z.string().max(500)).default({}),
  /** Path on this deployment the message links to, e.g. `/inbox`. */
  linkPath: z.string().max(500).nullable().default(null),
  /**
   * Idempotency for the delivery itself. Two outbox messages carrying the same
   * key send one email -- which is what makes the outbox's own at-least-once
   * redelivery safe to point at somebody's inbox.
   */
  dedupeKey: z.string().min(1).max(200),
});
export type NotificationRequest = z.infer<typeof notificationRequestSchema>;

/** The outbox `eventType` the dispatcher turns into a delivery job. */
export const NOTIFICATION_OUTBOX_EVENT = 'notification.requested';
